/* eslint-disable no-unused-vars */
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { IOptionAnyValue } from 'bricks/types';
import { SortDirection } from 'types/sort';
import { IProFormaReturnsData } from 'views/ProFormaTable/types';
import { IFilter } from './ReportTypes/PipelineReport/ConfigurationSidebar/Filters/types';
import { AttributeKeysMap } from './ReportTypes/PipelineReport/ConfigurationSidebar/constants';

export enum ReportType {
    Aggregate = 'Combined Deal Report',
    Comparison = 'Comparison Report',
    Pipeline = 'Pipeline Report',
}

export enum ReportTypeId {
    Pipeline = 1,
    Comparison = 2,
    Aggregate = 3,
}

export const ReportTypeIdToReportTypeMap: Record<number, ReportType> = {
    1: ReportType.Pipeline,
    2: ReportType.Comparison,
    3: ReportType.Aggregate,
};

// build url params as ReportType to kebab-case strings
export const ReportTypeToUrlParamMap: Record<ReportType, string> = Object.fromEntries(
    Object.values(ReportType).map(value => [value as ReportType, value.toLowerCase().replace(/\s+/g, '-')]),
) as Record<ReportType, string>;

// Reverse the map from kebab-case strings to ReportType
export const ReportTypeUrlParamToTypeMap: Record<string, ReportType> = Object.fromEntries(
    Object.entries(ReportTypeToUrlParamMap).map(([reportType, urlParam]) => [urlParam, reportType as ReportType]),
) as Record<string, ReportType>;

export const ReportTypeIdToNameMap: Record<number, string> = {
    1: 'Pipeline',
    2: 'Comparison',
    3: 'Combined Deal',
};

export interface IReportType {
    type: ReportType;
    name: string;
    description: string;
    icon: IconDefinition;
    disabledTooltip: JSX.Element;
}

export interface IDeal {
    id: number;
    dealName: string;
    dealType: string;
    assetType?: string;
    project: string;
    projectType: string;
    defaultProFormaId: number;
}

export interface IDealSelection {
    deal: IDeal;
    proFormaId: number;
}

export type AttributeKey = keyof typeof AttributeKeysMap;

export interface IReportsQueryParams {
    pageSize: number;
    currentPage: number;
    orgId: string;
    configured_filters?: {
        deal_ids?: number[];
        report_types?: number[];
    };
    searchValue?: string;
    sorting?: Record<string, SortDirection>;
}

export interface ProFormaData {
    id: number;
    selectedScenarioId?: number;
    selectedForecastMetric?: string;
}

export interface IPipelineReportQueryParams {
    pageSize: number;
    currentPage: number;
    orgId: string;
    selectedColumns: Array<string>;
    configuredFilters?: Array<IFilter>;
    proFormaData?: Array<ProFormaData>;
}

export interface IAggregateReportQueryParams {
    orgId: string;
    proFormaIds: number[];
}

export interface IReportsResponse {
    items: Array<IReport>;
    itemCount: number;
}

export interface IPipelineReportDealsResponse {
    items: Array<IPipelineReportDeals>;
    itemCount: number;
}

export interface IAggregateReportKeyStats {
    leveredEmx: number;
    leveredIrr: number;
    netSaleProceeds: number;
    stabilizedYieldOnCost: number;
    limitedPartnerIrr: number;
    generalPartnerIrr: number;
}

export interface MetricDetails {
    untrended: number;
    trended: number;
    sale: number;
    isHighlighted: boolean | null;
}

export interface TimelineData {
    startDate: string;
    operationStartDate: string | null;
    saleMonth: string | null;
    dealName: string;
    firstStabilizedMonth: string;
    developmentStartDate: string;
    developmentEndDate: string;
}

export interface IPartnerReturnsByProForma {
    dealName: string;

    gp1Name: string;
    gp1Emx: number;
    gp1Irr: number;
    gp1Roi: number;
    gp1NetProfit: number;
    gp1Contributions: number;

    gp2Name: string;
    gp2Emx: number;
    gp2Irr: number;
    gp2Roi: number;
    gp2NetProfit: number;
    gp2Contributions: number;

    lpName: string;
    lpEmx: number;
    lpIrr: number;
    lpRoi: number;
    lpNetProfit: number;
    lpContributions: number;
}

export interface IPartnerReturns {
    gpEmx: number;
    gpIrr: number;
    gpRoi: number;
    gpTotalContribution: number;
    gpTotalNetProfit: number;
    lpEmx: number;
    lpIrr: number;
    lpRoi: number;
    lpTotalContribution: number;
    lpTotalNetProfit: number;
    partnerReturnsByProforma: IPartnerReturnsByProForma[];
}

export interface IAggregateReportResponse {
    keyStats: IAggregateReportKeyStats;
    proFormaReturns: IProFormaReturnsData;
    timelineData: TimelineData[];
    partnerReturns: IPartnerReturns;
}

export interface ICustomAttribute {
    name: string;
    value: any;
}

export interface IPipelineReportDeals {
    deal_id: number;
    acquisition_price?: number;
    address?: string;
    asset_class?: number;
    equity_partner?: string;
    city?: string;
    construction_type?: number;
    county?: string;
    date_created?: string;
    deal_creator?: string;
    deal_name?: string;
    deal_type?: string;
    units?: string;
    description?: string;
    equity_multiple?: number;
    irr?: number;
    irr_using_project_actuals?: number;
    equity_multiple_using_project_actuals?: number;
    lead_source?: string;
    leasing_strategy?: string;
    deal_owner?: string;
    rank?: string;
    state?: string;
    subproject_stage_id?: number;
    target_profit_return?: number;
    target_return_on_cost?: number;
    target_return_on_equity?: number;
    zip_code?: string;
    custom_attributes?: Array<ICustomAttribute>;
}
export interface IReport {
    id: number;
    name: string;
    description: string;
    type: number;
    createdAt: string;
    metadata: any;
    deals: Array<string>;
}

export interface IReportDeal {
    dealId: number;
    proFormaId: number;
    dealName: string;
    proFormaName: string;
    dealType: string;
    assetType?: string;
}

interface IReportMetadata {
    sorting: Record<AttributeKey, SortDirection>;
    selectedColumns: Array<AttributeKey>;
    configuredFilters: Array<IFilter>;
}

export interface IReportDetails {
    id: number;
    name: string;
    description: string;
    type: number;
    createdAt: string;
    metadata: IReportMetadata;
    deals: Array<IReportDeal>;
}

interface IReportDealSelection {
    dealId: number;
    proFormaId: number;
}

export interface ICreateReportPostData {
    name: string;
    description: string;
    type: ReportTypeId;
    metadata?: IReportMetadata;
    deals?: Array<IReportDealSelection>;
}

export interface IUpdateReportPostData extends ICreateReportPostData {
    report_id: number;
}

export interface IReportName {
    name: string;
}

export interface IInvestmentMetricsData {
    roi?: number;
    emx?: number;
    irr?: number;
    isHighlighted?: boolean;
}

export interface IProjectionMetricsData {
    untrended?: number;
    trended?: number;
    sale?: number;
    isHighlighted?: boolean;
}

export interface ICostMetricsData {
    usagePercent?: number;
    costPerBuiltSf?: number;
    costPerUnit?: number;
    amount?: number;
    isHighlighted?: boolean;
}

export interface IDealData {
    id: number;
    name?: string;
    address?: string;
    city?: string;
    phase?: string;
    targetProfitReturn?: number;
    dateCreated?: string;
    surveyor?: string;
    description?: string;
    state?: string;
    dealType?: string;
    assetClass?: string;
    constructionType?: string;
    units?: string;
    progress?: number;
    leadSource?: string;
    leasingStrategy?: string;
    rank?: string;
    acquisitionPrice?: number;
    targetReturnOnCost?: number;
    targetReturnOnEquity?: number;
    irr?: string;
    equityMultiple?: string;
    dealCreator?: string;
    dealOwner?: string;
    developmentManager?: string;
    equityPartner?: string;
    lender?: string;
    legalCounsel?: string;
    architect?: string;
    engineer?: string;
    environmentalConsultant?: string;
    zipCode?: string;
    market?: string;
    submarket?: string;
    county?: string;
}

export interface IComparisonReportDetails {
    buildings: number;
    landArea: number;
    netRentableSf: number;
    parkingRatio: number;
    parkingSpaces: number;
    height: number;
    units: number;
}

export interface IComparisonReportPartnershipReturns {
    limitedPartner: IInvestmentMetricsData;
    generalPartner: IInvestmentMetricsData;
}

export interface IComparisonReportPropertyReturns {
    unlevered: IInvestmentMetricsData;
    levered: IInvestmentMetricsData;
}

export interface IComparisonReportDevelopmentReturns {
    yieldOnCost: IProjectionMetricsData;
    marketCapRate: IProjectionMetricsData;
    developmentSpread: IProjectionMetricsData;
}

export interface IComparisonReportUses {
    costPerBuiltSf: number;
    costPerUnit: number;
    amount: number;
    isHighlighted?: boolean;
}

export interface IComparisonReportUsesDetails {
    acquisitionCosts: IComparisonReportUses;
    hardCosts: IComparisonReportUses;
    softCosts: IComparisonReportUses;
    carryCosts: IComparisonReportUses;
    totalUses: IComparisonReportUses;
}

export interface IComparisonReportIncome {
    effectiveGrossRevenue: IProjectionMetricsData;
    operatingExpenses: IProjectionMetricsData;
    netOperatingIncome: IProjectionMetricsData;
    capitalExpenditures: IProjectionMetricsData;
    cashFlowFromOperations: IProjectionMetricsData;
    capRate: IProjectionMetricsData;
    valuation: IProjectionMetricsData;
    percentOfTotalProjectCost: IProjectionMetricsData;
    valuationPerUnit: IProjectionMetricsData;
}

export interface IComparisonReportData {
    dealId: number;
    dealName: string;
    dealType: number;
    dealTypeName?: string;
    dealAssetType?: string;
    proFormaId: number;
    proFormaName: string;
    details: IComparisonReportDetails;
    partnershipReturns: IComparisonReportPartnershipReturns;
    propertyReturns: IComparisonReportPropertyReturns;
    developmentReturns: IComparisonReportDevelopmentReturns;
    uses: IComparisonReportUsesDetails;
    income: IComparisonReportIncome;
}

export interface IDealOption extends IOptionAnyValue {
    defaultProFormaId: number;
}
